import * as React from 'react';
import type { JSX } from 'react';

import { SubtitleComponent } from './subtitle-component';
import { TitleChevron } from './title-chevron';
import { CardDescription, CardTitle, cn } from '../../../v2';
import { TextHighlight } from '../../text-highlight';
import type { DetailCardProps } from '../types';

export function IconTitleSubtitleBlock(
  props: Pick<
    DetailCardProps,
    | 'icon'
    | 'showSubtitleComponentWhenExpanded'
    | 'subtitle'
    | 'subtitleComponent'
    | 'title'
    | 'isHighlighted'
    | 'testId'
    | 'cardProps'
    | 'searchTerm'
  > & {
    expanded: boolean;
    setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    showChevron?: boolean;
  },
): JSX.Element {
  const {
    icon,
    title,
    subtitle,
    subtitleComponent,
    expanded,
    showChevron = true,
    setExpanded,
    isHighlighted,
    showSubtitleComponentWhenExpanded,
    testId,
    cardProps,
    searchTerm,
  } = props;
  const { titleClassName: cardTitleClassName, highlightProps } = cardProps ?? {};
  const titleClassName = cn('text-foreground', cardTitleClassName);

  const { titleClassName: highlightTitleClassName } = highlightProps ?? {};
  const currentTitleClassName = cn(titleClassName, {
    [cn('text-brand-400 dark:text-foreground', highlightTitleClassName)]: isHighlighted,
  });

  // Format subtitle - use passed component if available, otherwise use default SubtitleComponent
  const formattedSubtitle = !subtitle
    ? null
    : (() => {
        if (subtitleComponent) {
          return subtitleComponent;
        }

        // Default mode: use SubtitleComponent with highlighting if searchTerm provided
        if (searchTerm) {
          return <TextHighlight text={subtitle} searchTerm={searchTerm} />;
        }
        return <SubtitleComponent subtitle={subtitle} />;
      })();
  return (
    <div className="flex items-center gap-1 pl-1">
      {icon && <>{icon}</>}
      <div className="flex flex-col justify-between">
        <div
          className={cn('flex flex-col justify-between', {
            'text-brand-400 dark:text-foreground': isHighlighted,
          })}
        >
          <button className="text-start" onClick={() => setExpanded(!expanded)} type="button">
            <CardTitle
              className={cn('flex flex-row items-center', currentTitleClassName)}
              data-testid={`${testId}-title`}
            >
              <span>{searchTerm ? <TextHighlight text={title} searchTerm={searchTerm} /> : title}</span>
              {showChevron && <TitleChevron testId={testId} rotated={expanded} className={cn(title)} />}
            </CardTitle>
          </button>
          <CardDescription
            className={cn({
              'dark:text-foreground': isHighlighted,
            })}
            data-testid={`${testId}-subtitle`}
          >
            {formattedSubtitle}
          </CardDescription>
        </div>
        {subtitleComponent && (!expanded || (expanded && showSubtitleComponentWhenExpanded)) ? subtitleComponent : null}
      </div>
    </div>
  );
}
